import React, { useState } from 'react';
import { Star, MapPin, Users, ChevronLeft, Sparkles } from 'lucide-react';
import { useCountUp } from '../shared/useCountUp';
import { useLanguage } from '../../components/LanguageContext';
import '../shared/portals.css';

// ============================================================
// GuestPortal — short-stay (Airbnb-style) booking.
// Scope is intentionally light: the product decision on the guest
// flow is still open. This is a presentable placeholder — sample
// stays + a "booking coming soon" CTA — ready to expand once the
// guest journey is defined and wired to the booking backend.
//
// Guest never authenticates, so this landing page IS the guest
// entry screen — it carries the same ivory editorial masthead as
// the role select and the Owner / Tenant sign-ins.
// ============================================================

interface Props {
  onChangeRole?: () => void;
}

const STAYS = [
  { id: 's1', name: 'The Ivy · Skyline Suite', area: 'JVT, Dubai', price: 480, rating: 4.9, guests: 2, img: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1200&q=60' },
  { id: 's2', name: 'The Vybe · Marina View', area: 'Dubai Marina', price: 650, rating: 4.8, guests: 4, img: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1200&q=60' },
  { id: 's3', name: 'Haven Gardens · Loft', area: 'Downtown Dubai', price: 720, rating: 5.0, guests: 3, img: 'https://images.unsplash.com/photo-1560448204-603b3fc33ddc?auto=format&fit=crop&w=1200&q=60' },
];

const GuestPortal: React.FC<Props> = ({ onChangeRole }) => {
  const { t } = useLanguage();
  const [selected, setSelected] = useState<typeof STAYS[number] | null>(null);

  return (
    <div className="pt-screen gs-screen">
      <div className="gs-content">
        <header className="gs-head">
          <img src="/asset-desk-wordmark.png" alt="Asset Desk" className="pl-logo" />

          <div className="pl-rule" aria-hidden="true"><span /><i>&#9670;</i><span /></div>

          <div className="pl-eyebrow">{t('gpAccessEyebrow')}</div>
          <h1 className="pl-title">{t('gpBookStay')}</h1>
          <p className="pl-sub">{t('gpSub')}</p>
        </header>

        {!selected ? (
          <>
            <div className="gs-sectionlabel">{t('gpFeaturedStays')}</div>
            <div className="gs-grid pt-stagger">
              {STAYS.map((s) => (
                <button key={s.id} onClick={() => setSelected(s)} className="gs-stay">
                  <span className="gs-photo">
                    <span className="gs-img" style={{ backgroundImage: `url(${s.img})` }} />
                    <span className="gs-scrim" />
                    <span className="gs-rating">
                      <Star size={12} color="#D6B160" fill="#D6B160" /> {s.rating}
                    </span>
                    <span className="gs-caption">
                      <span className="gs-name">{s.name}</span>
                      <span className="gs-area"><MapPin size={12} /> {s.area}</span>
                    </span>
                  </span>
                  <span className="gs-meta">
                    <span className="gs-guests"><Users size={13} /> {s.guests} {t('gpGuests')}</span>
                    <span className="gs-price">
                      AED {s.price.toLocaleString('en-AE')}<span className="gs-per"> {t('gpPerNight')}</span>
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <StayDetail stay={selected} onBack={() => setSelected(null)} />
        )}

        {onChangeRole && (
          <button onClick={onChangeRole} className="pl-changerole">← {t('pcChangeRole')}</button>
        )}
      </div>
    </div>
  );
};

// ---------------- Stay detail ----------------
const StayDetail: React.FC<{ stay: typeof STAYS[number]; onBack: () => void }> = ({ stay, onBack }) => {
  const { t } = useLanguage();
  const price = useCountUp(stay.price);
  return (
    <div className="pt-fade gs-detail">
      <button onClick={onBack} className="gs-back">
        <ChevronLeft size={14} /> {t('gpAllStays')}
      </button>

      <div className="gs-sheet">
        <span className="gs-photo gs-photo--tall">
          <span className="gs-img" style={{ backgroundImage: `url(${stay.img})` }} />
          <span className="gs-scrim" />
          <span className="gs-caption">
            <span className="gs-name gs-name--lg">{stay.name}</span>
            <span className="gs-area">
              <MapPin size={13} /> {stay.area}
              <span className="gs-star"><Star size={12} color="#D6B160" fill="#D6B160" /> {stay.rating}</span>
            </span>
          </span>
        </span>

        <div className="gs-body">
          <div className="gs-pricebig">
            AED {price.toLocaleString('en-AE')}<span className="gs-per"> {t('gpPerNight')}</span>
          </div>

          <div className="gs-notice">
            <Sparkles size={16} color="#B99A4C" />
            <div>
              <div className="gs-notice-title">{t('gpBookingSoon')}</div>
              <p className="gs-notice-text">
                {t('gpBookingSoonText')}
              </p>
            </div>
          </div>

          <button disabled className="pl-btn">{t('gpReserve')}</button>
        </div>
      </div>
    </div>
  );
};

export default GuestPortal;
